// ============================================================
// FridgeManagement.jsx  –  Fresh Hills Admin
// ✅ Fully responsive: 320px → 1440px
// ✅ Modal slides up from bottom on mobile
// ✅ All features: Create, Edit, Delete, QR view, QR download
// ============================================================
import React, { useState, useEffect } from 'react';
import {
  getFridges, createFridge, updateFridge, deleteFridge,
  getApartments, createApartment, updateApartment,
  resolveUploadUrl,
} from '../../services/api';
import {
  QrCodeIcon, PlusIcon, BuildingOfficeIcon,
  TrashIcon, PencilIcon, ArrowDownTrayIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AdminLayout from './AdminLayout';
import api from '../../services/api';

const FridgeManagement = () => {
  const [fridges,    setFridges]    = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [view,       setView]       = useState('fridges');
  const [showFM,     setShowFM]     = useState(false);
  const [showAM,     setShowAM]     = useState(false);
  const [fridgeForm, setFridgeForm] = useState({ apartment_id:'', location:'' });
  const [aptForm,    setAptForm]    = useState({ name:'', address:'', status:'active' });
  const [editFridge, setEditFridge] = useState(null);
  const [editApt,    setEditApt]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [regen,      setRegen]      = useState(false);

  const loadData = () => Promise.all([
    getFridges().then(r    => setFridges(r.data.fridges       || [])).catch(() => {}),
    getApartments().then(r => setApartments(r.data.apartments || [])).catch(() => {}),
  ]);

  useEffect(() => { setLoading(true); loadData().finally(() => setLoading(false)); }, []);

  /* ── Fridge submit ── */
  const handleFridgeSubmit = async (e) => {
    e.preventDefault();
    if (!fridgeForm.apartment_id) { toast.error('Select an apartment'); return; }
    setSaving(true);
    try {
      const res = editFridge
        ? await updateFridge(editFridge.id, fridgeForm)
        : await createFridge(fridgeForm);
      if (res.data.success) {
        toast.success(editFridge ? 'Fridge updated!' : 'Fridge created! 🎉');
        if (!editFridge) {
          const qp = res.data.fridge?.qr_path || res.data.fridge?.qr_code;
          if (qp) window.open(resolveUploadUrl(qp), '_blank');
        }
        closeFM(); loadData();
      } else toast.error(res.data.message || 'Failed');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  /* ── Apartment submit ── */
  const handleAptSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = editApt
        ? await updateApartment(editApt.id, aptForm)
        : await createApartment(aptForm);
      if (res.data.success) {
        toast.success(editApt ? 'Apartment updated!' : 'Apartment created!');
        closeAM(); loadData();
      } else toast.error(res.data.message || 'Failed');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  /* ── Delete fridge ── */
  const handleDelete = async (f) => {
    if (!window.confirm(`Delete fridge ${f.fridge_id}?\nThis cannot be undone.`)) return;
    try {
      const res = await deleteFridge(f.id);
      if (res.data.success) { toast.success('Deleted'); setFridges(p => p.filter(x => x.id !== f.id)); }
      else toast.error(res.data.message || 'Delete failed');
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  /* ── Regenerate all QR ── */
  const handleRegen = async () => {
    if (!window.confirm('Regenerate all QR codes with production URL?')) return;
    setRegen(true);
    try {
      const res = await api.post('/qr/regenerate-all');
      if (res.data.success) {
        toast.success(`✅ Fixed ${res.data.updated} QR codes → ${res.data.frontend_url}`);
        loadData();
      } else toast.error(res.data.message || 'Failed');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setRegen(false); }
  };

  /* ── QR helpers ── */
  const openQR = (p) => {
    if (!p) { toast.error('QR not available'); return; }
    window.open(resolveUploadUrl(p), '_blank');
  };
  const downloadQR = async (f) => {
    if (!f.qr_code) { toast.error('QR not available'); return; }
    const url = resolveUploadUrl(f.qr_code);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `QR_${f.fridge_id}_${(f.apartment_name || 'fridge').replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(a.href);
      toast.success('QR downloaded!');
    } catch {
      window.open(url, '_blank');
      toast.success('QR opened — right-click → Save Image');
    }
  };

  /* ── Modal helpers ── */
  const openAddFM  = () => { setEditFridge(null); setFridgeForm({ apartment_id:'', location:'' }); setShowFM(true); };
  const openEditFM = (f) => { setEditFridge(f);   setFridgeForm({ apartment_id:f.apartment_id||'', location:f.location||'' }); setShowFM(true); };
  const closeFM    = () => { setShowFM(false); setEditFridge(null); setFridgeForm({ apartment_id:'', location:'' }); };
  const openAddAM  = () => { setEditApt(null); setAptForm({ name:'', address:'', status:'active' }); setShowAM(true); };
  const openEditAM = (a) => { setEditApt(a);   setAptForm({ name:a.name||'', address:a.address||'', status:a.status||'active' }); setShowAM(true); };
  const closeAM    = () => { setShowAM(false); setEditApt(null); setAptForm({ name:'', address:'', status:'active' }); };

  const SC = {
    active:      { bg:'#ecfdf5', color:'#059669', border:'#a7f3d0' },
    inactive:    { bg:'#fff1f2', color:'#dc2626', border:'#fecdd3' },
    maintenance: { bg:'#fef3c7', color:'#d97706', border:'#fde68a' },
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    /* ─── HEADER ROW ─────────────────────────────────────────── */
    .fm-hdr {
      display:flex; align-items:flex-start; justify-content:space-between;
      margin-bottom:1rem; flex-wrap:wrap; gap:0.65rem;
    }
    @media(min-width:560px){ .fm-hdr{ align-items:center; margin-bottom:1.5rem; gap:0.85rem; } }
    @media(min-width:768px){ .fm-hdr{ margin-bottom:2rem; gap:1rem; } }

    .fm-ta h1 { font-size:1.15rem; font-weight:900; color:#111827; }
    @media(min-width:480px){ .fm-ta h1{ font-size:1.35rem; } }
    @media(min-width:768px){ .fm-ta h1{ font-size:1.6rem; } }
    .fm-ta p { font-size:0.72rem; color:#64748b; font-weight:600; margin-top:0.08rem; }
    @media(min-width:480px){ .fm-ta p{ font-size:0.82rem; } }
    @media(min-width:768px){ .fm-ta p{ font-size:0.88rem; } }

    .fm-ctrl { display:flex; align-items:center; gap:0.4rem; flex-wrap:wrap; }
    @media(min-width:480px){ .fm-ctrl{ gap:0.55rem; } }
    @media(min-width:640px){ .fm-ctrl{ gap:0.75rem; } }

    /* Tabs */
    .fm-tabs { display:flex; background:#f1f5f9; border-radius:9999px; padding:0.18rem; }
    .fm-tab {
      padding:0.32rem 0.6rem; border-radius:9999px;
      font-weight:700; font-size:0.68rem; border:none; cursor:pointer;
      transition:all 0.2s; font-family:'Nunito',sans-serif;
      color:#64748b; background:none; white-space:nowrap;
    }
    @media(min-width:400px){ .fm-tab{ padding:0.36rem 0.75rem; font-size:0.74rem; } }
    @media(min-width:560px){ .fm-tab{ padding:0.4rem 1rem; font-size:0.8rem; } }
    @media(min-width:768px){ .fm-tab{ padding:0.45rem 1.25rem; font-size:0.85rem; } }
    .fm-tab.on { background:white; color:#059669; box-shadow:0 2px 8px rgba(0,0,0,0.08); }

    /* Add button */
    .fm-add {
      display:flex; align-items:center; gap:0.28rem;
      padding:0.38rem 0.72rem; border-radius:9999px;
      color:white; font-weight:800; font-size:0.7rem; border:none; cursor:pointer;
      transition:all 0.3s; font-family:'Nunito',sans-serif;
      box-shadow:0 3px 10px rgba(16,185,129,0.3); white-space:nowrap;
    }
    @media(min-width:400px){ .fm-add{ padding:0.42rem 0.88rem; font-size:0.76rem; } }
    @media(min-width:560px){ .fm-add{ padding:0.5rem 1.15rem; font-size:0.82rem; } }
    @media(min-width:768px){ .fm-add{ padding:0.6rem 1.4rem; font-size:0.9rem; } }
    .fm-add svg { width:0.78rem; height:0.78rem; }
    @media(min-width:560px){ .fm-add svg{ width:0.88rem; height:0.88rem; } }
    .fm-add:hover { transform:translateY(-1px); box-shadow:0 5px 14px rgba(16,185,129,0.35); }

    /* ─── FIX QR BANNER ──────────────────────────────────────── */
    .fm-warn {
      background:linear-gradient(135deg,#fef3c7,#fffbeb);
      border:1.5px solid #fde68a; border-radius:0.85rem;
      padding:0.65rem 0.9rem; margin-bottom:1rem;
      display:flex; align-items:center; justify-content:space-between;
      flex-wrap:wrap; gap:0.55rem;
    }
    @media(min-width:560px){ .fm-warn{ border-radius:1rem; padding:0.85rem 1.25rem; margin-bottom:1.5rem; } }
    .fm-warn-t { font-size:0.72rem; font-weight:700; color:#92400e; }
    @media(min-width:480px){ .fm-warn-t{ font-size:0.8rem; } }
    .fm-warn-t code { background:#fde68a; padding:0.08rem 0.32rem; border-radius:0.28rem; font-family:monospace; }
    .fm-regen {
      display:flex; align-items:center; gap:0.3rem;
      padding:0.38rem 0.85rem; border-radius:9999px;
      background:linear-gradient(135deg,#f59e0b,#d97706); color:white;
      font-weight:800; font-size:0.7rem; border:none; cursor:pointer;
      font-family:'Nunito',sans-serif; transition:all 0.3s; white-space:nowrap;
    }
    @media(min-width:480px){ .fm-regen{ padding:0.48rem 1.1rem; font-size:0.78rem; } }
    @media(min-width:640px){ .fm-regen{ padding:0.5rem 1.25rem; font-size:0.82rem; } }
    .fm-regen:hover { transform:translateY(-1px); box-shadow:0 4px 12px rgba(245,158,11,0.35); }
    .fm-regen:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
    .fm-regen svg { width:0.75rem; height:0.75rem; }
    @keyframes spin { to{ transform:rotate(360deg); } }
    .ic-spin { animation:spin 0.7s linear infinite; }

    /* ─── STAT ROW ───────────────────────────────────────────── */
    .fm-stats {
      display:grid; grid-template-columns:repeat(3,1fr);
      gap:0.6rem; margin-bottom:1rem;
    }
    @media(min-width:480px){ .fm-stats{ gap:0.9rem; margin-bottom:1.35rem; } }
    @media(min-width:768px){ .fm-stats{ gap:1.25rem; margin-bottom:2rem; } }
    .fm-stat {
      background:white; border-radius:0.875rem; padding:0.75rem 0.5rem;
      border:1.5px solid #f1f5f9; text-align:center;
    }
    @media(min-width:480px){ .fm-stat{ padding:1rem 0.75rem; border-radius:1.1rem; } }
    @media(min-width:768px){ .fm-stat{ padding:1.25rem 1rem; border-radius:1.25rem; } }
    .fm-sn { font-size:1.2rem; font-weight:900; }
    @media(min-width:480px){ .fm-sn{ font-size:1.5rem; } }
    @media(min-width:768px){ .fm-sn{ font-size:1.75rem; } }
    .fm-sl { font-size:0.58rem; font-weight:700; color:#64748b; margin-top:0.12rem; }
    @media(min-width:480px){ .fm-sl{ font-size:0.68rem; } }
    @media(min-width:768px){ .fm-sl{ font-size:0.78rem; } }

    /* ─── GRID ───────────────────────────────────────────────── */
    .fm-grid { display:grid; grid-template-columns:1fr; gap:0.75rem; }
    @media(min-width:440px){ .fm-grid{ grid-template-columns:repeat(2,1fr); gap:0.85rem; } }
    @media(min-width:900px){ .fm-grid{ grid-template-columns:repeat(3,1fr); gap:1.1rem; } }
    @media(min-width:1200px){ .fm-grid{ gap:1.25rem; } }

    /* ─── CARD ───────────────────────────────────────────────── */
    .fm-card {
      background:white; border-radius:1.1rem;
      padding:1rem; border:1px solid #f1f5f9;
      box-shadow:0 4px 12px rgba(0,0,0,0.04); transition:all 0.3s;
    }
    @media(min-width:480px){ .fm-card{ border-radius:1.3rem; padding:1.25rem; } }
    @media(min-width:768px){ .fm-card{ border-radius:1.5rem; padding:1.5rem; } }
    .fm-card:hover { transform:translateY(-3px); box-shadow:0 14px 26px rgba(0,0,0,0.08); border-color:#d1fae5; }

    .fm-ctop { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:0.75rem; }
    @media(min-width:480px){ .fm-ctop{ margin-bottom:0.9rem; } }
    .fm-icon {
      width:36px; height:36px; border-radius:0.65rem; flex-shrink:0;
      background:linear-gradient(135deg,#ecfdf5,#e0f2fe);
      display:flex; align-items:center; justify-content:center; font-size:1.15rem;
    }
    @media(min-width:480px){ .fm-icon{ width:40px; height:40px; border-radius:0.75rem; font-size:1.3rem; } }
    @media(min-width:768px){ .fm-icon{ width:44px; height:44px; font-size:1.4rem; } }
    .fm-badge {
      font-size:0.58rem; font-weight:700; padding:0.18rem 0.58rem;
      border-radius:9999px; border:1.5px solid; white-space:nowrap;
    }
    @media(min-width:480px){ .fm-badge{ font-size:0.66rem; padding:0.22rem 0.7rem; } }
    @media(min-width:768px){ .fm-badge{ font-size:0.7rem; padding:0.25rem 0.75rem; } }

    .fm-cname { font-size:0.84rem; font-weight:800; color:#111827; margin-bottom:0.2rem; }
    @media(min-width:480px){ .fm-cname{ font-size:0.92rem; } }
    @media(min-width:768px){ .fm-cname{ font-size:1rem; } }
    .fm-csub { font-size:0.68rem; color:#94a3b8; margin-bottom:0.15rem; }
    @media(min-width:480px){ .fm-csub{ font-size:0.76rem; } }
    @media(min-width:768px){ .fm-csub{ font-size:0.82rem; } }

    /* QR thumbnail */
    .fm-qrbox {
      width:100%; margin-top:0.6rem; border-radius:0.6rem;
      border:1px solid #e5e7eb; overflow:hidden;
      display:flex; align-items:center; gap:0.55rem;
      padding:0.48rem 0.6rem; background:#f9fafb; cursor:pointer; transition:all 0.2s;
    }
    @media(min-width:480px){ .fm-qrbox{ border-radius:0.72rem; margin-top:0.75rem; padding:0.58rem 0.72rem; gap:0.65rem; } }
    @media(min-width:768px){ .fm-qrbox{ border-radius:0.75rem; padding:0.6rem 0.75rem; } }
    .fm-qrbox:hover { background:#f0fdf4; border-color:#a7f3d0; }
    .fm-qrbox img {
      width:40px; height:40px; object-fit:contain;
      border-radius:0.32rem; flex-shrink:0;
      background:white; border:1px solid #e5e7eb;
    }
    @media(min-width:480px){ .fm-qrbox img{ width:46px; height:46px; } }
    @media(min-width:768px){ .fm-qrbox img{ width:52px; height:52px; } }
    .fm-qr-lbl { font-size:0.62rem; font-weight:700; color:#059669; margin-bottom:0.08rem; }
    @media(min-width:480px){ .fm-qr-lbl{ font-size:0.68rem; } }
    @media(min-width:768px){ .fm-qr-lbl{ font-size:0.72rem; } }
    .fm-qr-url { font-size:0.54rem; color:#94a3b8; word-break:break-all; line-height:1.3; }
    @media(min-width:480px){ .fm-qr-url{ font-size:0.58rem; } }
    @media(min-width:768px){ .fm-qr-url{ font-size:0.6rem; } }

    /* ─── ACTION BUTTONS ─────────────────────────────────────── */
    .fm-acts { display:flex; gap:0.35rem; margin-top:0.75rem; flex-wrap:wrap; }
    @media(min-width:480px){ .fm-acts{ gap:0.4rem; margin-top:0.9rem; } }
    @media(min-width:768px){ .fm-acts{ gap:0.45rem; margin-top:1rem; } }
    .fm-btn {
      flex:1; display:flex; align-items:center; justify-content:center; gap:0.22rem;
      padding:0.38rem 0.15rem; border-radius:0.5rem;
      font-weight:700; font-size:0.62rem; border:1.5px solid; cursor:pointer;
      transition:all 0.2s; font-family:'Nunito',sans-serif;
      white-space:nowrap; min-width:0;
    }
    @media(min-width:400px){ .fm-btn{ padding:0.42rem 0.2rem; font-size:0.66rem; border-radius:0.55rem; } }
    @media(min-width:480px){ .fm-btn{ padding:0.46rem 0.25rem; font-size:0.7rem; border-radius:0.6rem; } }
    @media(min-width:768px){ .fm-btn{ padding:0.5rem 0.35rem; font-size:0.74rem; border-radius:0.65rem; } }
    .fm-btn svg { width:0.65rem; height:0.65rem; flex-shrink:0; }
    @media(min-width:480px){ .fm-btn svg{ width:0.72rem; height:0.72rem; } }
    @media(min-width:768px){ .fm-btn svg{ width:0.78rem; height:0.78rem; } }

    .b-qr   { background:#faf5ff; color:#7c3aed; border-color:#ddd6fe; }
    .b-qr:hover   { background:#7c3aed; color:white; border-color:#7c3aed; }
    .b-dl   { background:#ecfdf5; color:#059669; border-color:#a7f3d0; }
    .b-dl:hover   { background:#059669; color:white; border-color:#059669; }
    .b-view { background:#eff6ff; color:#2563eb; border-color:#bfdbfe; }
    .b-view:hover { background:#2563eb; color:white; border-color:#2563eb; }
    .b-edit { background:#fefce8; color:#ca8a04; border-color:#fde047; }
    .b-edit:hover { background:#ca8a04; color:white; border-color:#ca8a04; }
    .b-del  { background:#fff1f2; color:#dc2626; border-color:#fecdd3; }
    .b-del:hover  { background:#dc2626; color:white; border-color:#dc2626; }
    .b-add  { background:#ecfdf5; color:#059669; border-color:#a7f3d0; }
    .b-add:hover  { background:#059669; color:white; border-color:#059669; }

    /* Empty */
    .fm-empty {
      grid-column:1/-1; padding:2.5rem 1.25rem; text-align:center;
      color:#94a3b8; background:white; border-radius:1.25rem; border:1px solid #f1f5f9;
    }
    @media(min-width:480px){ .fm-empty{ padding:4rem 2rem; border-radius:1.5rem; } }
    .fm-empty svg { width:2.5rem; height:2.5rem; margin:0 auto 0.75rem; opacity:0.35; display:block; }
    @media(min-width:480px){ .fm-empty svg{ width:3.5rem; height:3.5rem; } }

    /* ─── MODAL ──────────────────────────────────────────────── */
    /* On mobile: slides up from bottom (sheet style)
       On ≥540px: centred dialog                                 */
    .fm-mbg {
      position:fixed; inset:0; background:rgba(15,23,42,0.5);
      display:flex; align-items:flex-end; justify-content:center;
      z-index:200; backdrop-filter:blur(4px); padding:0;
    }
    @media(min-width:540px){ .fm-mbg{ align-items:center; padding:1rem; } }

    .fm-modal {
      background:white; border-radius:1.35rem 1.35rem 0 0;
      width:100%; max-width:100%;
      box-shadow:0 -8px 32px rgba(0,0,0,0.14);
      overflow:hidden; max-height:92vh;
      display:flex; flex-direction:column;
    }
    @media(min-width:540px){
      .fm-modal{
        border-radius:1.75rem; max-width:460px; max-height:90vh;
        box-shadow:0 25px 60px -10px rgba(0,0,0,0.25);
      }
    }

    .fm-mhdr {
      padding:1rem 1.15rem;
      display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
    }
    @media(min-width:480px){ .fm-mhdr{ padding:1.5rem; } }
    .fm-mhdr h3 { font-size:0.9rem; font-weight:900; color:white; }
    @media(min-width:480px){ .fm-mhdr h3{ font-size:1.05rem; } }
    @media(min-width:640px){ .fm-mhdr h3{ font-size:1.1rem; } }
    .fm-mx {
      width:28px; height:28px; border-radius:50%;
      background:rgba(255,255,255,0.2); border:none; color:white;
      cursor:pointer; display:flex; align-items:center; justify-content:center;
      font-size:0.9rem; flex-shrink:0;
    }
    @media(min-width:480px){ .fm-mx{ width:32px; height:32px; font-size:1rem; } }

    .fm-mbody { padding:1rem 1.15rem; overflow-y:auto; flex:1; }
    @media(min-width:480px){ .fm-mbody{ padding:1.5rem; } }

    .fm-fld { margin-bottom:0.9rem; }
    @media(min-width:480px){ .fm-fld{ margin-bottom:1.1rem; } }
    .fm-lbl { display:block; font-size:0.74rem; font-weight:700; color:#374151; margin-bottom:0.38rem; }
    @media(min-width:480px){ .fm-lbl{ font-size:0.82rem; margin-bottom:0.45rem; } }
    .fm-inp, .fm-sel {
      width:100%; padding:0.58rem 0.85rem;
      border:1.5px solid #e5e7eb; border-radius:0.8rem;
      font-size:0.85rem; font-family:'Nunito',sans-serif; font-weight:600;
      color:#111827; background:#f9fafb; outline:none; transition:all 0.2s;
    }
    @media(min-width:480px){ .fm-inp,.fm-sel{ padding:0.65rem 1rem; border-radius:0.875rem; } }
    .fm-inp:focus, .fm-sel:focus {
      border-color:#059669; background:white;
      box-shadow:0 0 0 3px rgba(5,150,105,0.1);
    }

    .fm-mftr {
      display:flex; justify-content:flex-end; gap:0.55rem;
      padding:0.85rem 1.15rem; border-top:1px solid #f1f5f9; flex-shrink:0;
    }
    @media(min-width:480px){ .fm-mftr{ padding:1.25rem 1.5rem; gap:0.75rem; } }
    .fm-cancel {
      padding:0.5rem 1rem; border-radius:9999px;
      background:#f8fafc; border:1.5px solid #e2e8f0;
      color:#64748b; font-weight:700; cursor:pointer;
      font-family:'Nunito',sans-serif; font-size:0.82rem;
    }
    @media(min-width:480px){ .fm-cancel{ padding:0.6rem 1.4rem; } }
    .fm-save {
      padding:0.5rem 1.2rem; border-radius:9999px;
      color:white; font-weight:800; border:none; cursor:pointer;
      font-family:'Nunito',sans-serif; transition:all 0.3s;
      box-shadow:0 4px 12px rgba(16,185,129,0.3); font-size:0.82rem;
    }
    @media(min-width:480px){ .fm-save{ padding:0.6rem 1.75rem; } }
    .fm-save:hover { transform:translateY(-1px); }
    .fm-save:disabled { opacity:0.5; cursor:not-allowed; transform:none; }

    /* Loading */
    .fm-ld { display:flex; justify-content:center; align-items:center; height:60vh; }
    .fm-spin {
      width:2.5rem; height:2.5rem; border-radius:50%;
      border:3px solid #f3f4f6; border-top-color:#059669;
      animation:spin 0.7s linear infinite;
    }
    @media(min-width:480px){ .fm-spin{ width:3rem; height:3rem; } }
  `;

  if (loading) return (
    <AdminLayout>
      <style>{css}</style>
      <div className="fm-ld"><div className="fm-spin" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* ── HEADER ── */}
      <div className="fm-hdr">
        <div className="fm-ta">
          <h1>{view === 'fridges' ? '❄️ Fridge Management' : '🏢 Apartment Management'}</h1>
          <p>{view === 'fridges'
            ? `${fridges.length} fridge${fridges.length !== 1 ? 's' : ''} registered`
            : `${apartments.length} apartment${apartments.length !== 1 ? 's' : ''} registered`}
          </p>
        </div>
        <div className="fm-ctrl">
          <div className="fm-tabs">
            <button className={`fm-tab${view === 'fridges' ? ' on' : ''}`} onClick={() => setView('fridges')}>❄️ Fridges</button>
            <button className={`fm-tab${view === 'apartments' ? ' on' : ''}`} onClick={() => setView('apartments')}>🏢 Apartments</button>
          </div>
          {view === 'fridges'
            ? <button className="fm-add" style={{ background:'linear-gradient(135deg,#10b981,#059669)' }} onClick={openAddFM}><PlusIcon />Add Fridge</button>
            : <button className="fm-add" style={{ background:'linear-gradient(135deg,#2563eb,#7c3aed)' }} onClick={openAddAM}><PlusIcon />Add Apartment</button>
          }
        </div>
      </div>

      {/* ── FIX QR BANNER ── */}
      {view === 'fridges' && (
        <div className="fm-warn">
          <div className="fm-warn-t">⚠️ QR codes showing <code>localhost</code>? Fix with one click.</div>
          <button className="fm-regen" onClick={handleRegen} disabled={regen}>
            <ArrowPathIcon className={regen ? 'ic-spin' : ''} />
            {regen ? 'Regenerating…' : 'Fix All QR Codes'}
          </button>
        </div>
      )}

      {/* ── APARTMENT STATS ── */}
      {view === 'apartments' && (
        <div className="fm-stats">
          <div className="fm-stat"><div className="fm-sn" style={{ color:'#2563eb' }}>{apartments.length}</div><div className="fm-sl">Total Apartments</div></div>
          <div className="fm-stat"><div className="fm-sn" style={{ color:'#059669' }}>{apartments.filter(a => a.status === 'active').length}</div><div className="fm-sl">Active</div></div>
          <div className="fm-stat"><div className="fm-sn" style={{ color:'#7c3aed' }}>{fridges.length}</div><div className="fm-sl">Total Fridges</div></div>
        </div>
      )}

      {/* ── FRIDGES GRID ── */}
      {view === 'fridges' ? (
        <div className="fm-grid">
          {fridges.length === 0 ? (
            <div className="fm-empty">
              <QrCodeIcon />
              <p style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:'0.3rem' }}>No fridges yet</p>
              <p style={{ fontSize:'0.78rem' }}>Add your first fridge to get started</p>
            </div>
          ) : fridges.map(f => {
            const sc    = SC[f.status] || SC.active;
            const qrUrl = resolveUploadUrl(f.qr_code);
            return (
              <div key={f.id} className="fm-card">
                <div className="fm-ctop">
                  <div className="fm-icon">❄️</div>
                  <span className="fm-badge" style={{ background:sc.bg, color:sc.color, borderColor:sc.border }}>
                    {f.status || 'active'}
                  </span>
                </div>
                <div className="fm-cname">{f.apartment_name || 'Unknown Apartment'}</div>
                <div className="fm-csub">🆔 {f.fridge_id}</div>
                <div className="fm-csub">📍 {f.location || 'No location set'}</div>
                <div className="fm-csub" style={{ fontSize:'0.62rem', color:'#c4cdd6', marginTop:'0.18rem' }}>
                  Added {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
                </div>

                {/* QR thumbnail — click to open full size */}
                {qrUrl && (
                  <div className="fm-qrbox" onClick={() => openQR(f.qr_code)} title="Click to view full QR">
                    <img src={qrUrl} alt={`QR ${f.fridge_id}`} onError={e => { e.target.style.display='none'; }} />
                    <div style={{ minWidth:0, flex:1 }}>
                      <div className="fm-qr-lbl">📱 Scan to open store</div>
                      <div className="fm-qr-url">{qrUrl}</div>
                    </div>
                  </div>
                )}

                <div className="fm-acts">
                  <button className="fm-btn b-qr"   onClick={() => openQR(f.qr_code)}><QrCodeIcon />View QR</button>
                  <button className="fm-btn b-dl"   onClick={() => downloadQR(f)}><ArrowDownTrayIcon />Download</button>
                  <button className="fm-btn b-view" onClick={() => window.open(`/fridge-store/${f.fridge_id}`, '_blank')}>Store</button>
                  <button className="fm-btn b-edit" onClick={() => openEditFM(f)}><PencilIcon /></button>
                  <button className="fm-btn b-del"  onClick={() => handleDelete(f)}><TrashIcon /></button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── APARTMENTS GRID ── */
        <div className="fm-grid">
          {apartments.length === 0 ? (
            <div className="fm-empty">
              <BuildingOfficeIcon />
              <p style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:'0.3rem' }}>No apartments yet</p>
              <p style={{ fontSize:'0.78rem' }}>Create apartments to assign fridges</p>
            </div>
          ) : apartments.map(a => {
            const sc     = a.status === 'active' ? SC.active : SC.inactive;
            const fCount = fridges.filter(f => String(f.apartment_id) === String(a.id)).length;
            return (
              <div key={a.id} className="fm-card">
                <div className="fm-ctop">
                  <div className="fm-icon">🏢</div>
                  <span className="fm-badge" style={{ background:sc.bg, color:sc.color, borderColor:sc.border }}>
                    {a.status || 'active'}
                  </span>
                </div>
                <div className="fm-cname">{a.name}</div>
                <div className="fm-csub">{a.address || 'No address'}</div>
                <div className="fm-csub" style={{ fontSize:'0.62rem', color:'#c4cdd6', marginTop:'0.18rem' }}>
                  Added {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                </div>
                <div className="fm-acts">
                  <button className="fm-btn b-view">❄️ {fCount} Fridge{fCount !== 1 ? 's' : ''}</button>
                  <button className="fm-btn b-add" onClick={() => {
                    setFridgeForm({ apartment_id: a.id, location:'' });
                    setEditFridge(null); setShowFM(true);
                  }}><PlusIcon />Add Fridge</button>
                  <button className="fm-btn b-edit" onClick={() => openEditAM(a)}><PencilIcon />Edit</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── FRIDGE MODAL — bottom sheet on mobile ── */}
      {showFM && (
        <div className="fm-mbg" onClick={e => e.target === e.currentTarget && closeFM()}>
          <div className="fm-modal">
            <div className="fm-mhdr" style={{ background:'linear-gradient(135deg,#059669,#0891b2)' }}>
              <h3>{editFridge ? '✏️ Edit Fridge' : '❄️ Add New Fridge'}</h3>
              <button className="fm-mx" onClick={closeFM}>✕</button>
            </div>
            <form onSubmit={handleFridgeSubmit}>
              <div className="fm-mbody">
                <div className="fm-fld">
                  <label className="fm-lbl">Select Apartment *</label>
                  <select className="fm-sel" value={fridgeForm.apartment_id}
                    onChange={e => setFridgeForm({ ...fridgeForm, apartment_id:e.target.value })} required>
                    <option value="">Choose an apartment</option>
                    {apartments.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  {apartments.length === 0 && (
                    <p style={{ fontSize:'0.72rem', color:'#dc2626', marginTop:'0.38rem' }}>
                      No apartments found.{' '}
                      <button type="button"
                        style={{ color:'#059669', fontWeight:700, background:'none', border:'none', cursor:'pointer', fontSize:'0.72rem' }}
                        onClick={() => { closeFM(); openAddAM(); }}>
                        Create one first →
                      </button>
                    </p>
                  )}
                </div>
                <div className="fm-fld">
                  <label className="fm-lbl">Location / Floor *</label>
                  <input className="fm-inp" type="text" value={fridgeForm.location}
                    onChange={e => setFridgeForm({ ...fridgeForm, location:e.target.value })}
                    placeholder="e.g. Ground Floor, Block A" required />
                </div>
              </div>
              <div className="fm-mftr">
                <button type="button" className="fm-cancel" onClick={closeFM}>Cancel</button>
                <button type="submit" className="fm-save" disabled={saving || !apartments.length}
                  style={{ background:'linear-gradient(135deg,#10b981,#059669)' }}>
                  {saving ? '⏳ Saving…' : editFridge ? 'Update Fridge' : 'Create Fridge'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── APARTMENT MODAL ── */}
      {showAM && (
        <div className="fm-mbg" onClick={e => e.target === e.currentTarget && closeAM()}>
          <div className="fm-modal">
            <div className="fm-mhdr" style={{ background:'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
              <h3>{editApt ? '✏️ Edit Apartment' : '🏢 Add New Apartment'}</h3>
              <button className="fm-mx" onClick={closeAM}>✕</button>
            </div>
            <form onSubmit={handleAptSubmit}>
              <div className="fm-mbody">
                <div className="fm-fld">
                  <label className="fm-lbl">Apartment Name *</label>
                  <input className="fm-inp" type="text" value={aptForm.name}
                    onChange={e => setAptForm({ ...aptForm, name:e.target.value })}
                    placeholder="e.g. Sunrise Towers" required />
                </div>
                <div className="fm-fld">
                  <label className="fm-lbl">Address</label>
                  <input className="fm-inp" type="text" value={aptForm.address}
                    onChange={e => setAptForm({ ...aptForm, address:e.target.value })}
                    placeholder="e.g. Main Street, Block A" />
                </div>
                <div className="fm-fld">
                  <label className="fm-lbl">Status</label>
                  <select className="fm-sel" value={aptForm.status}
                    onChange={e => setAptForm({ ...aptForm, status:e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="fm-mftr">
                <button type="button" className="fm-cancel" onClick={closeAM}>Cancel</button>
                <button type="submit" className="fm-save" disabled={saving}
                  style={{ background:'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
                  {saving ? '⏳ Saving…' : editApt ? 'Update Apartment' : 'Create Apartment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default FridgeManagement;